import React from "react";
import { connect } from "react-redux";
import ReactTooltip from "react-tooltip";

import QRCode from "react-qr-code";

import Utilities from "../../foundation/Utility";
import Logger from "../../foundation/Logger";

import { IAppState } from "../../state/Store";
import { addAlert, handleError } from "../../state/system/Alerts";
import {
    deleteAddress,
    deleteEmail,
    deleteNumber,
    getNewEmail,
    getNewNumber,
    getNewAddress,
    initialiseTitleDetails,
    saveAddress,
    saveEmail,
    saveNewAddress,
    saveNewEmail,
    saveNewNumber,
    saveNumber,
} from "../../state/core/Contact";
import {
    initialiseUserPasswordDetails,
    initialiseVersionPreferences,
    setVersionPreference,
    updateUser,
    updateUserPasswordless,
    updateUserPicture,
    updateUserTwoFactorType,
} from "../../state/core/User";
import { initialiseGeographyDetails } from "../../state/core/Geography";

import Panel from "../../components/panels/Panel";
import Dialog from "../../components/dialog/Dialog";
import DialogContent from "../../components/dialog/DialogContent";
import DialogFooter from "../../components/dialog/DialogFooter";
import DialogCancel from "../../components/dialog/DialogCancel";
import DialogOK from "../../components/dialog/DialogOK";
import Croppie from "../../components/cropping/Cropping";
import PasswordEditor from "../../components/passwords/PasswordEditor";
import AddressEditor from "../../components/addresses/AddressEditor";
import PhoneNumberEditor from "../../components/phoneNumbers/PhoneNumberEditor";
import Toolbar from "../../components/toolbar/Toolbar";
import ToolbarButton from "../../components/toolbar/ToolbarButton";

import AlertEntry, { AlertTypes } from "../../types/system/AlertEntry";
import State from "../../types/core/State";
import Country from "../../types/core/Country";
import User from "../../types/core/User";
import PhoneNumber from "../../types/core/PhoneNumber";
import EmailAddress from "../../types/core/EmailAddress";
import Address from "../../types/core/Address";
import TwoFactorDisplayEntry from "../../types/twoFactor/TwoFactorDisplayEntry";
import Fido2Challenge from "../../types/fido2/Fido2Challenge";
import Fido2Response from "../../types/fido2/Fido2Response";
import PasswordConfig from "../../types/passwords/PasswordConfig";
import PasswordChange from "../../types/passwords/PasswordChange";
import RenderOrientations from "../../types/enums/RenderOrientation";
import ButtonThemes from "../../types/enums/ButtonThemes";
import { FlexDirection, Side, Size } from "../../types/system/Layout";
import {
    initialiseTwoFactorDetails,
    initialiseUserContactDetails,
} from "../../state/system/Session";
import { setPageLoading } from "../../state/system/UIConfiguration";
import { unwrappedDispatch } from "../../foundation/Dispatching";
import Textbox from "../../components/inputform/Textbox";
import InputForm from "../../components/inputform/InputForm";
import InputFormField from "../../components/inputform/InputFormField";
import InputFieldTypes from "../../types/enums/ImportFieldTypes";
import Utility from "../../foundation/Utility";
import { TitleType } from "../../types/core/Person";
import RenderOrientation from "../../types/enums/RenderOrientation";
import { PersonGenders } from "../../types/enums/PersonGenders";
import RecordsPanel from "../../components/panels/RecordsPanel";
import NumberTypes from "../../types/enums/NumberTypes";
import TTSDebouncer from "../../foundation/TTSDebouncer";
import EmailAddressEditor from "../../components/emailaddresses/EmailAddressEditor";
import Text from "../../components/text/Text";
import Info from "../../components/info/Info";
import List from "../../components/list/List";
import ListItem from "../../components/list/ListItem";
import Button from "../../components/buttons/Button";
import IconStyles from "../../types/enums/IconStyles";
import { AppContext } from "../../types/system/AppContext";
import Fido2CredentialMakeResult from "../../types/fido2/Fido2CredentialMakeResult";
import { ClientSystemPreference } from "../../types/core/ClientSystemVersion";
import RadioList from "../../components/inputform/RadioList";
import GeneralContainer from "../../components/containers/GeneralContainer";
import Datastore from "../../communication/Datastore";

import profile from "../../graphics/profile.svg";

import "./Profile.scss";

interface ProfileProps {
    status?: string;
    contactStatus?: string;
    user?: User;
    profilePicture?: string;
    passwordConfig: number;
    twoFactorDisplay?: TwoFactorDisplayEntry[];
    // Geography Props
    states?: Record<number, State[]>;
    countries?: Country[];
    hasSSO?: boolean;
    versions?: ClientSystemPreference[];

    setPageLoading?: (loading: boolean) => void;
    handleError?: (error: any) => void;
    initialiseGeographyDetails: () => Promise<void>;
    initialiseTitleDetails: () => Promise<void>;
    initialiseUserContactDetails: () => Promise<void>;
    initialiseTwoFactorDetails: () => Promise<void>;
    initialiseUserPasswordDetails: () => Promise<void>;
    initialiseVersionPreferences: () => Promise<void>;
    updateUserTwoFactorType: (type: TwoFactorDisplayEntry) => Promise<void>;
    updateUserPasswordless: (value: boolean) => Promise<void>;
    updateUser: (user: User[]) => Promise<void>;
    updateUserPicture: (userId: number, image: string) => Promise<void>;
    // Phone Number State
    getNewNumber: () => Promise<PhoneNumber>;
    saveNewUserNumber: (user: User, number: PhoneNumber) => Promise<void>;
    saveUserNumber: (number: PhoneNumber) => Promise<void>;
    deleteUserNumber: (number: PhoneNumber) => Promise<void>;
    // Email State
    getNewEmail: () => Promise<EmailAddress>;
    saveNewUserEmail: (user: User, email: EmailAddress) => Promise<void>;
    saveUserEmail: (email: EmailAddress) => Promise<void>;
    deleteUserEmail: (email: EmailAddress) => Promise<void>;
    // Address State
    getNewAddress: () => Promise<Address>;
    saveNewUserAddress: (user: User, address: Address) => Promise<void>;
    saveUserAddress: (address: Address) => Promise<void>;
    deleteUserAddress: (address: Address) => Promise<void>;
}

interface ProfileState {
    shouldReload: boolean;
    loading: boolean;
    user?: User;
    dirty: boolean;
    pictureOpen: boolean;
    pictureLoading: boolean;
    pictureWorking: string;
    pictureCropped: string;
    pictureSaveAllowed: boolean;
    // Password State
    password: PasswordConfig;
    passwordOpen: boolean;
    passwordLoading: boolean;
    passwordCurrent: string;
    passwordChanged: string;
    passwordCurrentRef: React.RefObject<HTMLInputElement>;
    passwordChangedRef: React.RefObject<HTMLInputElement>;
    canSubmitPassword: boolean;

    usernameValid: string;
    usernameValidDebouncer: TTSDebouncer;

    // 2FA State
    twofaOpen: boolean;
    twofaLoading: boolean;
    twofaType: TwoFactorDisplayEntry;
    twofaMethod: string;
    twofaTarget: string;
    twofaAuthSecret: string;
    twofaAuthValue: string;
    twofaAuthDisplay: string;
    twofaResponse: string;
    twofaEntry1: React.RefObject<HTMLInputElement>;
    twofaShowSecret: boolean;
    // Passwordless State
    passwordlessChallenge: string;
}

class Profile extends React.Component<ProfileProps, ProfileState> {
    static contextType = AppContext;
    context!: React.ContextType<typeof AppContext>;

    state = {
        user: null,

        pictureWorking: profile,
        passwordCurrent: "",
        passwordChanged: "",
        passwordCurrentRef: React.createRef<HTMLInputElement>(),
        passwordChangedRef: React.createRef<HTMLInputElement>(),
        canSubmitPassword: false,

        usernameValid: "",
        usernameValidDebouncer: new TTSDebouncer(500),

        twofaResponse: "",
        twofaEntry1: React.createRef<HTMLInputElement>(),
    } as ProfileState;

    static getDerivedStateFromProps(
        props: ProfileProps,
        state: ProfileState,
    ): ProfileState {
        if (!state.user || state.shouldReload) {
            return {
                ...state,
                user: props.user,
                shouldReload: false,
            };
        }
        return null;
    }

    async componentDidMount() {
        ReactTooltip.rebuild();

        try {
            this.props.setPageLoading(true);

            await Promise.all([
                this.props.initialiseGeographyDetails(),
                this.props.initialiseUserContactDetails(),
                this.props.initialiseTitleDetails(),
                this.props.initialiseTwoFactorDetails(),
                this.props.initialiseUserPasswordDetails(),
                this.props.initialiseVersionPreferences(),
            ]);
        } finally {
            this.props.setPageLoading(false);
        }
    }

    async saveBasic() {
        await this.props.updateUser([this.state.user]);
        this.setState(() => ({ shouldReload: true }));
    }
    async updatePassword() {
        var chars = Math.floor(this.props.passwordConfig / 10000);
        var raw = this.props.passwordConfig - chars * 10000;

        this.setState(() => ({
            password: {
                passwordMinLength: chars,
                passwordIncludeUpperLetter: (Math.pow(2, 0) & raw) > 0,
                passwordIncludeLowerLetter: (Math.pow(2, 1) & raw) > 0,
                passwordIncludeNumber: (Math.pow(2, 2) & raw) > 0,
                passwordIncludeSpecial: (Math.pow(2, 3) & raw) > 0,
                passwordExcludeNames: (Math.pow(2, 4) & raw) > 0,
            },
            passwordCurrent: "",
            passwordChanged: "",
            passwordOpen: true,
            canSubmitPassword: false,
        }));
    }
    async savePasswordChange() {
        this.setState(() => ({ passwordLoading: true }));

        try {
            await this.completePasswordChange();

            this.setState(() => ({ passwordOpen: false }));
        } catch (error) {
        } finally {
            this.setState(() => ({ passwordLoading: false }));
        }
    }
    async completePasswordChange() {
        await Datastore.post("api/Authentication/Session/UpdatePassword", {
            current: this.state.passwordCurrent,
            password: this.state.passwordChanged,
        });

        addAlert({
            type: AlertTypes.Alert,
            title: "Password Change",
            text: "Your password was successfully updated.",
        } as AlertEntry);
    }

    editPicture() {
        this.setState(() => ({
            pictureWorking: this.props.profilePicture?.toDataUrl() ?? profile,
            pictureOpen: true,
        }));
    }
    async editPictureChanged(files: FileList) {
        let fileUrl = await Utilities.fileTobase64(files[0]);
        this.setState(() => ({ pictureWorking: fileUrl }));
    }
    editPictureCropStarted() {
        this.setState(() => ({ pictureSaveAllowed: false }));
    }
    editPictureCropped(image: string) {
        this.setState(() => ({
            pictureCropped: image,
            pictureSaveAllowed: true,
        }));
    }
    async saveEditPicture() {
        this.setState(() => ({ pictureLoading: true }));
        const scaledImage = (
            await Utility.resizeImage(this.state.pictureCropped, 400)
        ).fromDataUrl();
        await this.props.updateUserPicture(this.state.user.id, scaledImage);
        this.setState(() => ({ pictureOpen: false, pictureLoading: false }));
    }

    addEmail() {
        this.setState(prev => ({
            user: {
                ...prev.user,
                emails: prev.user.emails.concat([
                    {
                        id: 0,
                        primary: false,
                        unsubscribed: false,
                        address: "",
                    } as EmailAddress,
                ]),
            } as User,
        }));
    }
    updateEmail(address: EmailAddress, updated: EmailAddress) {
        if (!this.state.user) return;

        let updatedCollection = [...this.state.user.emails];
        let index = this.state.user.emails.indexOf(address);
        if (updated.primary)
            updatedCollection.forEach(item => (item.primary = false));
        updatedCollection.splice(index, 1, updated);
        this.setState(() => ({
            user: {
                ...this.state.user,
                emails: updatedCollection,
            } as User,
        }));

        if (updated.primary) {
            this.setState(() => ({ usernameValid: "autorenew" }));

            this.state.usernameValidDebouncer.call(() => {
                this.validateUsername(updated.address);
            });
        }
    }
    async validateUsername(username: string) {
        if (!this.state.user) return;

        let valid = true;
        if (username.length < 1) valid = false;
        else if (!Utilities.validEmailAddress(username)) valid = false;
        else if (
            await Datastore.getType<boolean>(
                `api/Users/${
                    this.state.user.id ?? 0
                }/Username/${encodeURIComponent(username)}/InUse`,
            )
        )
            valid = false;

        this.setState(() => ({
            usernameValid: !valid ? "error" : "check",
        }));
    }
    deleteEmailAddress(address: EmailAddress) {
        let index = this.state.user.emails.indexOf(address);
        let updated = [...this.state.user.emails];
        updated.splice(index, 1);
        this.setState(() => ({
            user: {
                ...this.state.user,
                emails: updated,
            } as User,
        }));
    }

    addAddress() {
        this.setState(() => ({
            user: {
                ...this.state.user,
                addresses: this.state.user.addresses.concat([
                    {
                        id: 0,
                        line1: "",
                        line2: "",
                        city: "",
                        state: -1,
                        country: -1,
                        postcode: "",
                    } as Address,
                ]),
            } as User,
        }));
    }
    updateEditAddress(address: Address, updated: Address) {
        let index = this.state.user.addresses.indexOf(address);
        let updatedCollection = [...this.state.user.addresses];
        updatedCollection.splice(index, 1, updated);
        this.setState(() => ({
            user: {
                ...this.state.user,
                addresses: updatedCollection,
            } as User,
        }));
    }
    deleteAddress(address: Address) {
        let index = this.state.user.addresses.indexOf(address);
        let updated = [...this.state.user.addresses];
        updated.splice(index, 1);
        this.setState(() => ({
            user: {
                ...this.state.user,
                addresses: updated,
            } as User,
        }));
    }

    addNumber() {
        this.setState(() => ({
            user: {
                ...this.state.user,
                numbers: this.state.user.numbers.concat([
                    {
                        id: 0,
                        type: NumberTypes.Phone,
                        phoneNumber: "",
                    } as PhoneNumber,
                ]),
            } as User,
        }));
    }
    updateEditNumber(number: PhoneNumber, updated: PhoneNumber) {
        let index = this.state.user.numbers.indexOf(number);
        let updatedCollection = [...this.state.user.numbers];
        updatedCollection.splice(index, 1, updated);
        this.setState(() => ({
            user: {
                ...this.state.user,
                numbers: updatedCollection,
            } as User,
        }));
    }
    deleteNumber(number: PhoneNumber) {
        let index = this.state.user.numbers.indexOf(number);
        let updated = [...this.state.user.numbers];
        updated.splice(index, 1);
        this.setState(() => ({
            user: {
                ...this.state.user,
                numbers: updated,
            } as User,
        }));
    }

    // 2FA Logic
    async change2FAState(type: TwoFactorDisplayEntry, state: boolean) {
        this.setState(() => ({ loading: true }));

        try {
            if (state) {
                let target = (
                    await Datastore.get(
                        `api/Authentication/Session/CheckTwoFactorMode/${type.value}`,
                    )
                ).toString();
                let method = "";

                switch (type.value) {
                    case 1:
                        method = "email address";
                        break;
                    case 2:
                        method = "mobile number";
                        break;
                    case 3:
                        method = "Authenticator";
                        break;
                }

                let twofaAuthSecret: string = null;
                let twofaAuthValue: string = null;
                let twofaAuthDisplay: string = null;
                if (type.value === 3) {
                    twofaAuthSecret = Utilities.authenticatorCode(10);
                    twofaAuthDisplay = `${twofaAuthSecret.substring(
                        0,
                        3,
                    )} ${twofaAuthSecret.substring(
                        4,
                        7,
                    )} ${twofaAuthSecret.substring(
                        8,
                        11,
                    )} ${twofaAuthSecret.substring(12, 15)}`;
                    twofaAuthValue = (
                        await Datastore.get(
                            `api/Authentication/Session/GetAuthenticatorURL/${twofaAuthSecret}`,
                        )
                    ).toString();
                } else
                    await Datastore.get(
                        `api/Authentication/Session/SendInitialSecurityCode/${type.value}`,
                    );

                this.setState(() => ({
                    twofaType: type,
                    twofaMethod: method,
                    twofaTarget: target,
                    twofaAuthSecret: twofaAuthSecret,
                    twofaAuthValue: twofaAuthValue,
                    twofaAuthDisplay: twofaAuthDisplay,
                    twofaOpen: true,
                    loading: false,
                }));

                setTimeout(() => this.state.twofaEntry1.current.focus(), 200);
            } else {
                await Datastore.get(
                    "api/Authentication/Session/SetTwoFactorConfig/-1/null",
                );

                this.props.updateUserTwoFactorType(null);

                addAlert({
                    type: AlertTypes.Alert,
                    title: "Two Factor Disabled",
                    text: "Two factor authentication has been disabled, disabling two factor authentication which reduces your security, we recommend you re-enable one of the two factor methods.",
                } as AlertEntry);
            }
        } finally {
            this.setState(() => ({ loading: false }));
        }
    }
    changeTwoFAResponse(value: string) {
        this.setState(() => ({ twofaResponse: value }));
    }
    async verifyTwoFA() {
        this.setState(() => ({ twofaLoading: true }));

        try {
            if (this.state.twofaType.value < 3)
                await Datastore.get(
                    `api/Authentication/Session/SetTwoFactorConfig/${
                        this.state.twofaType.value
                    }/${encodeURIComponent(this.state.twofaResponse)}`,
                );
            else
                await Datastore.get(
                    `api/Authentication/Session/CheckAuthenticatorResult/${encodeURIComponent(
                        this.state.twofaAuthSecret,
                    )}/${encodeURIComponent(this.state.twofaResponse)}`,
                );

            addAlert({
                type: AlertTypes.Alert,
                title: "Two Factor Enabled",
                text: "Two factor authentication has been enabled, you will now be required to enter a two factor code each time you sign in.",
            } as AlertEntry);

            this.props.updateUserTwoFactorType(this.state.twofaType);

            this.setState(() => ({ twofaOpen: false }));
        } finally {
            this.setState(() => ({ twofaLoading: false }));
        }
    }

    // Fido Logic
    private async setupPasswordless() {
        if (await this.context.verifyCredentials()) {
            let credentialOptions: Fido2Challenge;
            try {
                this.props.setPageLoading(true);

                credentialOptions = await Datastore.getType<Fido2Challenge>(
                    "api/Authentication/Session/Passwordless/Registration/Challenge",
                );
            } catch (error) {
                Logger.error(error);
                this.props.handleError(
                    new Error(
                        "There was a problem intiating the registration process.",
                    ),
                );
                return;
            } finally {
                this.props.setPageLoading(false);
            }

            if (credentialOptions.options.status !== "ok") {
                this.props.handleError(
                    new Error(credentialOptions.options.errorMessage),
                );
                return;
            }

            const createOptions = {
                rp: {
                    id: credentialOptions.options.rp.id,
                    name: credentialOptions.options.rp.name,
                },
                user: {
                    name: credentialOptions.options.user.name,
                    id: String(credentialOptions.options.user.id).fromBase64(),
                    displayName: credentialOptions.options.user.displayName,
                },
                challenge: String(
                    credentialOptions.options.challenge,
                ).fromBase64(),
                pubKeyCredParams:
                    credentialOptions.options.pubKeyCredParams.map(item => ({
                        alg: item.alg,
                        type: "public-key",
                    })),
                attestation: "direct",
                authenticatorSelection: {
                    residentKey: "required",
                    userVerification: "required",
                },
            } as PublicKeyCredentialCreationOptions;

            let newCredential: PublicKeyCredential;
            try {
                newCredential = (await navigator.credentials.create({
                    publicKey: createOptions,
                })) as PublicKeyCredential;
            } catch (error) {
                Logger.error(error);

                addAlert({
                    icon: "key",
                    styleColor: "red",
                    title: "Passwordless Authentication",
                    text:
                        error.message ??
                        "The operation either timed out or was not allowed.",
                } as AlertEntry);

                return;
            }

            let response =
                newCredential.response as AuthenticatorAttestationResponse;
            try {
                this.props.setPageLoading(true);

                let fido2Response: Fido2Response = {
                    response: {
                        id: Utilities.base64Encode(newCredential.id),
                        rawId: Utilities.base64FromArrayBuffer(
                            newCredential.rawId,
                        ),
                        type: newCredential.type,
                        extensions: newCredential.getClientExtensionResults(),
                        response: {
                            AttestationObject: Utilities.base64FromArrayBuffer(
                                response.attestationObject,
                            ),
                            clientDataJson: Utilities.base64FromArrayBuffer(
                                response.clientDataJSON,
                            ),
                        },
                    },
                    challenge: credentialOptions.challenge,
                };

                let result =
                    await Datastore.postType<Fido2CredentialMakeResult>(
                        "api/Authentication/Session/Passwordless/Registration/Validate",
                        fido2Response,
                    );

                if (result.status !== "ok") throw result.errorMessage;

                addAlert({
                    icon: "key",
                    type: AlertTypes.Alert,
                    title: "Passwordless Authentication",
                    text: "Passwordless authentication was successfully setup for your account.",
                } as AlertEntry);

                this.props.updateUserPasswordless(true);

                this.setState(() => ({ shouldReload: true }));
            } catch (error) {
                Logger.error(error);

                addAlert({
                    icon: "key",
                    styleColor: "red",
                    title: "Passwordless Authentication",
                    text: "There was a problem enabling passwordless authentication, please contact support.",
                } as AlertEntry);
            } finally {
                this.props.setPageLoading(false);
            }
        }
    }

    private async removeFido2(item: string) {
        if (await this.context.verifyCredentials()) {
            const result = await addAlert({
                icon: "delete_forever",
                styleColor: "red",
                title: "Device Passkey",
                text: `Are you sure you want to remove the Passkey for ${item}.`,
                okText: "Yes",
            });

            if (!result.response) return;

            try {
                this.props.setPageLoading(true);

                await Datastore.post(
                    "api/Authentication/Session/Passwordless/Registration/Disable",
                    item,
                );

                this.props.updateUserPasswordless(false);

                this.setState(() => ({
                    shouldReload: true,
                }));
            } finally {
                this.props.setPageLoading(false);
            }
        }
    }

    //Application Versions
    async updateVersionPreference(
        version: ClientSystemPreference,
        setting: string,
    ) {
        await setVersionPreference(version.id, setting);
    }

    render() {
        return (
            <>
                <Panel
                    size="scaling-pane-max"
                    center={true}
                    bufferSide={Side.All}
                    clearanceSide={Side.Vertical}
                    clearanceSize={Size.Large}
                    flex={FlexDirection.Row}>
                    <div className="profile-photo-wrapper">
                        <img
                            src={
                                this.props.profilePicture?.toDataUrl() ??
                                profile
                            }
                            className="profile-picture"
                            alt="Profile"
                        />

                        <div
                            className="select-mask"
                            onClick={() => this.editPicture()}></div>
                    </div>

                    <div className="profile-details-wrapper pane">
                        <Panel>
                            <Toolbar
                                className="profile-toolbar"
                                direction={RenderOrientations.RightLeft}>
                                <ToolbarButton
                                    icon="save"
                                    text="Save"
                                    theme={ButtonThemes.Theme}
                                    data-tip="Save your changes to your profile details"
                                    data-place="left"
                                    disabled={!this.state.dirty}
                                    onClick={this.saveBasic.bind(this)}
                                />
                                <ToolbarButton
                                    text="Change Password"
                                    icon="key"
                                    onClick={this.updatePassword.bind(this)}
                                    data-tip="Update your sign-in password"
                                    data-place="left"
                                    visible={!this.props.hasSSO}
                                />
                            </Toolbar>

                            <h1 className="user-name">
                                {this.props.user?.fullName}
                            </h1>

                            <Textbox
                                fullWidth={true}
                                autoComplete="off"
                                placeholder="No Work Title"
                                className="work-title"
                                value={this.state.user?.workTitle ?? ""}
                                onChange={value =>
                                    this.setState(() => ({
                                        user: {
                                            ...this.state.user,
                                            workTitle: value,
                                        } as User,
                                    }))
                                }
                            />

                            <Panel
                                bufferSide={Side.All}
                                clearanceSide={Side.Top}
                                title="Basic Information">
                                <InputForm<User>
                                    type={User}
                                    binding={this.state.user}
                                    onChange={user =>
                                        this.setState(() => ({ user }))
                                    }
                                    fieldDisplay={RenderOrientation.Horizontal}
                                    columns={2}>
                                    <InputFormField
                                        type={InputFieldTypes.Select}
                                        field="title"
                                        label="Title"
                                        labelBold={true}
                                        options={Utility.enumToOptions(
                                            TitleType,
                                        )}
                                    />
                                </InputForm>
                                <InputForm<User>
                                    type={User}
                                    clearanceSide={Side.Top}
                                    clearanceSize={Size.Small}
                                    binding={this.state.user}
                                    onChange={user =>
                                        this.setState(() => ({ user }))
                                    }
                                    fieldDisplay={RenderOrientation.Horizontal}
                                    columns={2}>
                                    <InputFormField
                                        field="firstName"
                                        label="First Name"
                                        labelBold={true}
                                    />
                                    <InputFormField
                                        field="middleName"
                                        label="Middle Name"
                                        labelBold={true}
                                    />
                                    <InputFormField
                                        field="lastName"
                                        label="Last Name"
                                        labelBold={true}
                                    />
                                    <InputFormField
                                        type={InputFieldTypes.Select}
                                        field="gender"
                                        label="Gender"
                                        labelBold={true}
                                        options={Utility.enumToOptions(
                                            PersonGenders,
                                        )}
                                    />
                                </InputForm>
                            </Panel>

                            <Panel
                                bufferSide={Side.All}
                                clearanceSide={Side.Top}
                                title="Contact Information">
                                <RecordsPanel<EmailAddress>
                                    typeName="Email Address"
                                    additionalMessage="You must provide at least one email address to be used for login."
                                    clearanceSide={Side.Top}
                                    inlineRemove={true}
                                    noRecordTitle={true}
                                    records={this.state.user?.emails ?? []}
                                    canRemove={record => !record.primary}
                                    editor={record => (
                                        <EmailAddressEditor
                                            value={record}
                                            valid={this.state.usernameValid}
                                            onChange={updated =>
                                                this.updateEmail(
                                                    record,
                                                    updated,
                                                )
                                            }
                                        />
                                    )}
                                    onAdd={this.addEmail.bind(this)}
                                    onDelete={record =>
                                        this.deleteEmailAddress(record)
                                    }
                                />

                                <RecordsPanel<PhoneNumber>
                                    typeName="Phone Number"
                                    clearanceSide={Side.Top}
                                    inlineRemove={true}
                                    noRecordTitle={true}
                                    records={this.state.user?.numbers ?? []}
                                    editor={record => (
                                        <PhoneNumberEditor
                                            value={record}
                                            onChange={updated =>
                                                this.updateEditNumber(
                                                    record,
                                                    updated,
                                                )
                                            }
                                        />
                                    )}
                                    onAdd={this.addNumber.bind(this)}
                                    onDelete={record =>
                                        this.deleteNumber(record)
                                    }
                                />

                                <RecordsPanel<Address>
                                    typeName="Address"
                                    clearanceSide={Side.Top}
                                    records={this.state.user?.addresses ?? []}
                                    editor={record => (
                                        <AddressEditor
                                            value={record}
                                            onChange={updated =>
                                                this.updateEditAddress(
                                                    record,
                                                    updated,
                                                )
                                            }
                                        />
                                    )}
                                    onAdd={this.addAddress.bind(this)}
                                    onDelete={record =>
                                        this.deleteAddress(record)
                                    }
                                />
                            </Panel>
                        </Panel>

                        {!this.props.hasSSO &&
                            this.props.twoFactorDisplay.length > 0 &&
                            !this.props.user?.hasFido2 && (
                                <Panel
                                    bufferSide={Side.All}
                                    clearanceSide={Side.Top}
                                    title="Two Factor Authentication">
                                    <p className="info">
                                        This section allows you to configure a
                                        second factor which will be used during
                                        sign-in to verify your identity.
                                    </p>

                                    {this.props.twoFactorDisplay.map(
                                        twoFactorType => {
                                            return (
                                                <label
                                                    key={twoFactorType.value}
                                                    className="checkbox hasLabel"
                                                    style={{ marginRight: 10 }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            twoFactorType.enabled
                                                        }
                                                        onChange={ev =>
                                                            this.change2FAState(
                                                                twoFactorType,
                                                                ev.target
                                                                    .checked,
                                                            )
                                                        }
                                                    />

                                                    <span className="checkmark"></span>

                                                    <span className="text">
                                                        {twoFactorType.name}
                                                    </span>
                                                </label>
                                            );
                                        },
                                    )}
                                </Panel>
                            )}

                        {!this.props.hasSSO &&
                            !!(navigator.credentials || {}).create && (
                                <Panel
                                    bufferSide={Side.All}
                                    clearanceSide={Side.Top}
                                    secureAdd={true}
                                    onAdd={() => this.setupPasswordless()}
                                    title="Passwordless Authentication / Passkeys">
                                    <Info>
                                        This section allows you to configure
                                        passwordless authentication which allows
                                        you to use your device to authenticate
                                        you rather than a username and password.
                                    </Info>

                                    <List clearTop={true}>
                                        {this.state.user?.fido2Credentials.map(
                                            (credential, index) => (
                                                <ListItem
                                                    key={index}
                                                    bufferSide={
                                                        Side.Horizontal
                                                    }>
                                                    <Text inline={true}>
                                                        {credential}
                                                    </Text>
                                                    <Button
                                                        icon="close"
                                                        className="item-button"
                                                        iconStyle={
                                                            IconStyles.Red
                                                        }
                                                        tooltip="Remove this passwordless credential"
                                                        tooltipPlace="left"
                                                        onClick={() =>
                                                            this.removeFido2(
                                                                credential,
                                                            )
                                                        }
                                                    />
                                                </ListItem>
                                            ),
                                        )}
                                    </List>
                                </Panel>
                            )}

                        {!!this.props.versions?.length && (
                            <Panel
                                bufferSide={Side.All}
                                clearanceSide={Side.Top}
                                title="Application Version Preferences">
                                <Info>
                                    This section allows you to configure your
                                    preference on the version of certain
                                    applications.
                                </Info>

                                <Info clearanceSide={Side.Top}>
                                    For more information on Application
                                    Versions,
                                    <a
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        href="https://timetabling.elevio.help/en/articles/777">
                                        see article.
                                    </a>
                                </Info>

                                <List
                                    clearTop={true}
                                    joined={true}>
                                    {this.props.versions
                                        .slice()
                                        .sort(Utility.getSorter(["name"]))
                                        .map((version, index) => (
                                            <ListItem
                                                key={index}
                                                flex={FlexDirection.Column}
                                                bufferSide={Side.All}
                                                autoHeight={true}>
                                                <h2>{version.name}</h2>
                                                <GeneralContainer
                                                    clearanceSide={Side.Top}>
                                                    <RadioList<string>
                                                        display={
                                                            RenderOrientations.Horizontal
                                                        }
                                                        value={
                                                            version.versions.find(
                                                                v =>
                                                                    v.preferred,
                                                            )?.name
                                                        }
                                                        options={version.versions.map(
                                                            v => ({
                                                                value: v.name,
                                                                text: v.name,
                                                            }),
                                                        )}
                                                        onChange={value =>
                                                            this.updateVersionPreference(
                                                                version,
                                                                value,
                                                            )
                                                        }
                                                    />
                                                </GeneralContainer>
                                            </ListItem>
                                        ))}
                                </List>
                            </Panel>
                        )}
                    </div>
                </Panel>

                <Dialog
                    showDialog={this.state.pictureOpen}
                    showLoading={this.state.pictureLoading}
                    dialogIcon="image"
                    dialogTitle="Edit Profile Picture"
                    canResize={false}
                    zIndex={10}>
                    <DialogContent>
                        <Panel bufferSide={Side.All}>
                            <table style={{ width: "100%" }}>
                                <tbody>
                                    <tr>
                                        <td>
                                            <Croppie
                                                open={this.state.pictureOpen}
                                                url={this.state.pictureWorking}
                                                imageCropStarted={() =>
                                                    this.editPictureCropStarted()
                                                }
                                                imageCropped={image =>
                                                    this.editPictureCropped(
                                                        image,
                                                    )
                                                }
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <div className="profile-picture-wrapper">
                                                <label tabIndex={0}>
                                                    Choose File
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={ev =>
                                                            this.editPictureChanged(
                                                                ev.target.files,
                                                            )
                                                        }
                                                    />
                                                </label>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </Panel>
                    </DialogContent>
                    <DialogFooter>
                        <DialogOK
                            text="Save"
                            disabled={!this.state.pictureSaveAllowed}
                            onClick={() => this.saveEditPicture()}></DialogOK>
                        <DialogCancel
                            onClick={() =>
                                this.setState(() => ({ pictureOpen: false }))
                            }></DialogCancel>
                    </DialogFooter>
                </Dialog>

                <Dialog
                    showDialog={this.state.passwordOpen}
                    showLoading={this.state.passwordLoading}
                    dialogTitle="Update Password"
                    zIndex={10}>
                    <DialogContent>
                        <Panel bufferSide={Side.All}>
                            <PasswordEditor
                                className="password-change full"
                                confirmCurrent={true}
                                password={this.state.password}
                                passwordsChanged={(changes: PasswordChange) =>
                                    this.setState(() => ({
                                        passwordCurrent:
                                            changes.currentPassword,
                                        passwordChanged:
                                            changes.updatedPassword,
                                        canSubmitPassword: changes.valid,
                                    }))
                                }
                            />
                        </Panel>
                    </DialogContent>
                    <DialogFooter>
                        <DialogOK
                            text="Save"
                            disabled={!this.state.canSubmitPassword}
                            onClick={() =>
                                this.savePasswordChange()
                            }></DialogOK>
                        <DialogCancel
                            onClick={() =>
                                this.setState(() => ({ passwordOpen: false }))
                            }></DialogCancel>
                    </DialogFooter>
                </Dialog>

                <Dialog
                    showDialog={this.state.twofaOpen}
                    showLoading={this.state.twofaLoading}
                    dialogTitle="Enable Two Factor Authentication"
                    zIndex={10}>
                    <DialogContent>
                        <Panel bufferSide={Side.All}>
                            {this.state.twofaType?.value < 3 && (
                                <p className="info">
                                    We have sent a verification code to the{" "}
                                    {this.state.twofaMethod}{" "}
                                    {this.state.twofaTarget}, please enter this
                                    code below.
                                    <br />
                                    <br />
                                    <b>Note:</b> Enabling this second factor
                                    will disable any already enabled second
                                    factor.
                                </p>
                            )}
                            {this.state.twofaType?.value === 3 && (
                                <span>
                                    <span className="info">
                                        Please scan the below QR code with your
                                        chosen authenticator app, some examples
                                        are:
                                        <ul style={{ textAlign: "left" }}>
                                            <li>Google Authenticator</li>
                                            <li>Microsoft Authenticator</li>
                                            <li>LastPass Authenticator</li>
                                        </ul>
                                        <span style={{ textAlign: "center" }}>
                                            <span
                                                className="section-title"
                                                style={{ textAlign: "left" }}>
                                                <b>Note:</b> Enabling this
                                                second factor will disable any
                                                already existing second factor.
                                            </span>
                                        </span>
                                    </span>

                                    <Panel
                                        bufferSide={Side.All}
                                        center={true}
                                        clearanceSide={Side.Top}
                                        width={200}>
                                        <QRCode
                                            size={128}
                                            style={{
                                                display: "block",
                                                margin: "0 auto",
                                            }}
                                            value={
                                                this.state.twofaAuthValue
                                            }></QRCode>
                                    </Panel>

                                    {this.state.twofaShowSecret && (
                                        <Panel
                                            bufferSide={Side.All}
                                            center={true}
                                            clearanceSide={Side.Top}
                                            width={200}>
                                            <span
                                                style={{
                                                    display: "inline-block",
                                                    textAlign: "center",
                                                    width: "100%",
                                                }}>
                                                {this.state.twofaAuthDisplay}
                                            </span>
                                        </Panel>
                                    )}

                                    <div
                                        className="center top-clearance"
                                        style={{ width: "200px" }}>
                                        <input
                                            type="button"
                                            className="link-btn full"
                                            value={
                                                this.state.twofaShowSecret
                                                    ? "Hide Secret"
                                                    : "Can't Scan It?"
                                            }
                                            onClick={() =>
                                                this.setState(() => ({
                                                    twofaShowSecret:
                                                        !this.state
                                                            .twofaShowSecret,
                                                }))
                                            }
                                        />
                                    </div>
                                </span>
                            )}
                            <span style={{ textAlign: "center" }}>
                                <div className="top-clearance">
                                    <label>
                                        <h2 className="two-factor-code-title">
                                            Verification Code
                                        </h2>
                                        <input
                                            ref={this.state.twofaEntry1}
                                            type="text"
                                            className="two-factor-code"
                                            maxLength={6}
                                            value={this.state.twofaResponse}
                                            onChange={ev =>
                                                this.changeTwoFAResponse(
                                                    ev.target.value,
                                                )
                                            }
                                            placeholder="000000"
                                            autoComplete="one-time-code"
                                        />
                                    </label>
                                </div>
                            </span>
                        </Panel>
                    </DialogContent>
                    <DialogFooter>
                        <DialogOK
                            text="Verify"
                            onClick={() => this.verifyTwoFA()}></DialogOK>
                        <DialogCancel
                            onClick={() =>
                                this.setState(() => ({ twofaOpen: false }))
                            }></DialogCancel>
                    </DialogFooter>
                </Dialog>
            </>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { geography, user, contact } = state.db;
    return {
        states: geography.states,
        countries: geography.countries,
        status: user.status,
        contactStatus: user.contactStatus,
        titleStatus: contact.status,
        user: user.profileUser,
        profilePicture: user.profilePicture,
        passwordConfig: user.passwordConfig,
        titles: contact.titles,
        twoFactorDisplay: user.twoFactorDisplay,
        hasSSO: user.hasSSO,
        versions: user.systemVersions,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        setPageLoading: (loading: boolean) => dispatch(setPageLoading(loading)),
        handleError: (error: any) => dispatch(handleError(error)),
        initialiseGeographyDetails: () => initialiseGeographyDetails(),
        initialiseTitleDetails: () =>
            unwrappedDispatch(initialiseTitleDetails()),
        initialiseUserContactDetails: () =>
            unwrappedDispatch(initialiseUserContactDetails()),
        initialiseUserPasswordDetails: () =>
            unwrappedDispatch(initialiseUserPasswordDetails()),
        initialiseTwoFactorDetails: () =>
            unwrappedDispatch(initialiseTwoFactorDetails()),
        initialiseVersionPreferences: () =>
            unwrappedDispatch(initialiseVersionPreferences()),
        updateUserTwoFactorType: (type: TwoFactorDisplayEntry) =>
            unwrappedDispatch(updateUserTwoFactorType(type)),
        updateUserPasswordless: (value: boolean) =>
            unwrappedDispatch(updateUserPasswordless(value)),
        updateUser: (user: User[]) => unwrappedDispatch(updateUser(user)),
        updateUserPicture: (userId: number, image: string) =>
            unwrappedDispatch(updateUserPicture({ userId, image })),
        getNewNumber: () => unwrappedDispatch(getNewNumber()),
        saveNewUserNumber: (user: User, number: PhoneNumber) =>
            unwrappedDispatch(
                saveNewNumber({
                    personId: user.personID,
                    number: number,
                }),
            ),
        saveUserNumber: (number: PhoneNumber) =>
            unwrappedDispatch(saveNumber(number)),
        deleteUserNumber: (number: PhoneNumber) =>
            unwrappedDispatch(deleteNumber(number)),
        getNewEmail: () => unwrappedDispatch(getNewEmail()),
        saveNewUserEmail: (user: User, email: EmailAddress) =>
            unwrappedDispatch(
                saveNewEmail({
                    personId: user.personID,
                    email: email,
                }),
            ),
        saveUserEmail: (email: EmailAddress) =>
            unwrappedDispatch(saveEmail(email)),
        deleteUserEmail: (email: EmailAddress) =>
            unwrappedDispatch(deleteEmail(email)),
        getNewAddress: () => unwrappedDispatch(getNewAddress()),
        saveNewUserAddress: (user: User, address: Address) =>
            unwrappedDispatch(
                saveNewAddress({
                    personId: user.personID,
                    address: address,
                }),
            ),
        saveUserAddress: (address: Address) =>
            unwrappedDispatch(saveAddress(address)),
        deleteUserAddress: (address: Address) =>
            unwrappedDispatch(deleteAddress(address)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Profile);
